import React, { useEffect, useContext } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Flex } from 'components/shared/flex'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import { SelectField } from 'components/shared/input'
import { A4 } from 'components/shared/print'
import { selectYears, setPageSize, toDateText } from 'utils/util'
import moment from 'moment'
import styled from 'styled-components'
import _ from 'lodash'
import { RequestFormErrorSearchForm } from 'entities/request_form_error'
import { useFetchRequestFormErrorsApi } from 'api/request_form_error'
import { RequestFormErrorViewModel } from 'view_models/request_form_error_view_model'
import { Button } from 'react-bootstrap'

type Props = {
    history: H.History
}

const Th = styled.th`
    border: solid 1px black;
    padding: 5px;
    text-align: center;
`

const Td = styled.td`
    border: solid 1px black;
    padding: 5px;
    text-align: center;
`

const RequestFormErrorLedgerPage = ({ history }: Props) => {
    const globalState = useContext(GlobalStateContext)
    const initDate = { start: moment().startOf('month').format('YYYY-MM-DD'), end: moment().endOf('month').format('YYYY-MM-DD') }
    const periodForm = useForm<{ year: string; month: string }>({
        year: moment().format('YYYY'),
        month: moment().format('MM'),
    })

    const searchForm = useForm<RequestFormErrorSearchForm>({
        requestDateAfter: initDate.start,
        requestDateBefore: initDate.end,
    })
    const api = useFetchRequestFormErrorsApi(searchForm)

    useEffect(() => {
        api.execute()
        setPageSize('landscape')
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(api.loading)
    }, [api.loading])

    useEffectSkipFirst(() => {
        searchForm.update((f) => {
            f.requestDateAfter = moment(`${periodForm.object.year}-${periodForm.object.month}-01`).startOf('month').format('YYYY-MM-DD')
            f.requestDateBefore = moment(`${periodForm.object.year}-${periodForm.object.month}-01`).endOf('month').format('YYYY-MM-DD')
        })
    }, [periodForm.object])

    const chunks = () => {
        return _.chunk(api.response.results, 12)
    }

    return (
        <>
            <div className="no-print">
                <Flex style={{ margin: 20 }}>
                    <div style={{ marginRight: 10, width: 250 }}>
                        <SelectField className="" form={periodForm} attr="year" selectItems={selectYears()} />
                    </div>
                    <div style={{ width: 150 }}>
                        <SelectField
                            className=""
                            form={periodForm}
                            attr="month"
                            selectItems={Array(12)
                                .fill(0)
                                .map((_, m) => ({ label: `${m + 1}月`, value: ('0' + (m + 1)).slice(-2) }))}
                        />
                    </div>

                    <div>
                        <Button
                            style={{ width: 200 }}
                            onClick={() => {
                                window.print()
                            }}
                        >
                            印刷プレビュー
                        </Button>
                    </div>
                </Flex>
            </div>
            {chunks().map((chunk, cidx) => {
                return (
                    <A4 landscape padding={0} key={'chunk-' + cidx}>
                        <div style={{ padding: '30px 30px' }}>
                            <Flex style={{ fontSize: 11 }} justifyContent="space-between">
                                <div>
                                    <div>Form08.01 検査依頼情報・検査結果報告情報台帳</div>
                                    <div>Version2: 2021/10/20</div>
                                </div>
                                <div>
                                    Page: {cidx + 1} / {chunks().length}
                                </div>
                            </Flex>
                            <div style={{ fontSize: 24, fontWeight: 'bold', margin: '20px 0' }}>検査依頼情報・検査結果報告情報台帳</div>
                            <div style={{ fontSize: 10 }}>
                                <table style={{ border: 'solid 2px black', width: '100%' }}>
                                    <thead>
                                        <Th style={{ width: 200, textAlign: 'left' }}>医療機関等名</Th>
                                        <Th style={{ width: 130, textAlign: 'left' }}>
                                            カルテ番号 or
                                            <br />
                                            ユーザー名+生年月日
                                        </Th>
                                        <Th style={{ width: 80 }}>変更依頼日</Th>
                                        <Th style={{ width: 120, textAlign: 'left' }}>変更内容</Th>
                                        <Th style={{ width: 80 }}>対応日</Th>
                                        <Th style={{ width: 80 }}>対応者</Th>
                                        <Th style={{ width: 80 }}>確認日</Th>
                                        <Th style={{ width: 80 }}>確認者</Th>
                                        <Th style={{ verticalAlign: 'middle', textAlign: 'left' }}>備考</Th>
                                    </thead>
                                    <tbody>
                                        {chunk.map((item, index) => {
                                            const vm = new RequestFormErrorViewModel(item)
                                            return (
                                                <tr key={'disposal-ledger' + index}>
                                                    <Td style={{ padding: '0 5px', verticalAlign: 'middle', textAlign: 'left' }}>{item.institution?.name}</Td>
                                                    <Td style={{ padding: '5px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <div>カルテ番号: {item.karteNumber ?? 'なし'}</div>
                                                        <div>ユーザー名: {item.userName ?? 'なし'}</div>
                                                        <div>生年月日: {item.birthday ?? 'なし'}</div>
                                                    </Td>
                                                    <Td>{toDateText(item.requestDate)}</Td>
                                                    <Td style={{ padding: '5px', verticalAlign: 'middle', textAlign: 'left' }}>
                                                        <div>
                                                            {Object.keys(vm.items()).map((key, index) => {
                                                                const value = vm.items()[key]
                                                                return (
                                                                    <div key={'error-item-' + index}>
                                                                        {key}: {value ? value : 'なし'}
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    </Td>
                                                    <Td>{toDateText(item.correspondingDate)}</Td>
                                                    <Td>{item.correspondingUser?.fullName}</Td>
                                                    <Td>{toDateText(item.confirmationDate)}</Td>
                                                    <Td>{item.confirmationUser?.fullName}</Td>
                                                    <Td style={{ padding: '0 5px', verticalAlign: 'middle', textAlign: 'left' }}>{item.note}</Td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </A4>
                )
            })}
        </>
    )
}

export default withRouter(RequestFormErrorLedgerPage)
